import "./styles.scss";

import ReplayOutlined from "@mui/icons-material/ReplayOutlined";
import Visibility from "@mui/icons-material/Visibility";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import EastIcon from "@mui/icons-material/East";
import {
    CircularProgress,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tooltip,
} from "@mui/material";
import BankCard from "assets/img/svg/bankCard";
import { NotAllow } from "assets/img/svg/notAllow";
import { Box as ProvisionMenuIcon, Document } from "assets/img/svg/statementManagTable";
import variables from "assets/styles/_variables.module.scss";
import classNames from "classnames";
import BalanceUpdateModal from "components/BalanceUpdateModal";
import ModalBox from "components/Modal";
import CurrencyIcon from "components/shared/CurrencyIcon";
import { columns } from "constants/ManagementTableColumns";
import { PortalType } from "contexts/portal.context";
import format from "date-fns/format";
import { SmallBody } from "helpers/TypographyComponents";
import { formatNumber } from "helpers/utils";
import type { IHandleRequest } from "hooks/useAPIPagination";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useSelectRows } from "hooks/useSelectRow";
import type { Dispatch, SetStateAction } from "react";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import { fetchTermGroups } from "store/beneficiaryStore";
import { type GeneralTabTerm, recomputeTermRoyalties, type TermState } from "store/managementStore";
import { type PeriodState } from "store/periodStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { PeriodStatus } from "store/types";
import type { TSortOrder } from "types";

import AddSalesModal from "../AddSalesModal";
import EmptyState from "../EmptyState";
import { useTableColumnView } from "../LastElementTable/ViewColumnOptions/utils";
import ManagementTableAuthorComponent from "../ManagementTableAuthorComponent";
import ManagementTableTitleComponent from "../ManagementTableTitleComponent";
import type { TToggleDrawer } from "../ManagementTitleDetails";
import ManagementTitleDetails from "../ManagementTitleDetails";
import MessageBlock from "../MessageBlock";
import ModalBase from "../ModalBase";
import Pagination from "../Pagination";
import StyledButton from "../StyledButton";
import StyledCheckbox from "../StyledCheckbox";
import TableControl from "../TableControl";
import TableSkeletonRows, { TABLE_SKELETON_MAX_ROWS } from "../TableSkeletonRows";
import { ManagementModifyBalanceMenuItem } from "./ManagementModifyBalanceMenuItem";

const tHeadStyles = {
    maxWidth: 200,
    fontWeight: 600,
    fontSize: "12px",
    color: "#676767",
    width: "min-content",
    padding: "8px 16px",
    backgroundColor: "#F6F6F6",
    fontFamily: "'Poppins', sans-serif",
};

type TProps = {
    page: number;
    count: number;
    limitPagination: number;
    sortBy: string;
    sortOrder: TSortOrder;
    handleChangePage: (event: unknown, newPage: number) => void;
    handleChangeLimit: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleSort: (name: string) => void;
    activePeriod: number | undefined;
    setSelectedRows: Dispatch<SetStateAction<number[]>>;
    selectedRows: number[];
    closeTerm: (rows: number[]) => void;
    addSale: (titleId: number | null, termId: number) => Promise<unknown>;
    openProvisionModal: (term: GeneralTabTerm) => void;
    handleRequest: (params?: IHandleRequest) => void;
    getStats: (period: number) => void;
    periodStatus: PeriodStatus;
    /**
     * Handle search query
     */
    handleQuery: (e: React.ChangeEvent<HTMLInputElement>) => void;
    isRoyaltiesRecalculating?: boolean;
};
const formatDate = (date: string) => format(new Date(date), "PP");

type ManagementGeneralTableParams = Partial<PeriodState & TermState> & TProps;

function ManagementGeneralTable({
    page,
    count,
    limitPagination,
    sortBy,
    sortOrder,
    handleChangeLimit,
    handleChangePage,
    handleSort,
    handleRequest,
    generalTabData = [],
    termsLoadingStatus,
    periods = [],
    addSalesPendingStatus = [],
    addSalesCheckingStatus = [],
    closeTermsStatus,
    activePeriod,
    setSelectedRows,
    selectedRows,
    closeTerm,
    addSale,
    openProvisionModal,
    getStats,
    periodStatus,
    handleQuery,
    isRoyaltiesRecalculating = false,
}: ManagementGeneralTableParams) {
    const [t] = useTranslation();
    const nav = useNavigate();
    const dispatch = useAppDispatch();
    const portalType = useContext(PortalType);
    const periodClosed = periodStatus === PeriodStatus.CLOSED;
    const periodFuture = periodStatus === PeriodStatus.FUTURE;

    const balanceCell = useRef(null);

    const { showColumn } = useTableColumnView("management-general-table", portalType);

    const { allSelected, setAllSelected, handleSelectRow } = useSelectRows({ setSelectedRows, data: generalTabData });
    const [anchorEls, setAnchorEls] = useState<{
        [index: number]: HTMLElement | null;
    }>({});
    const [currentTerm, setCurrentTerm] = useState<GeneralTabTerm | null>(null);
    const [recomputingTermId, setRecomputingTermId] = useState<number | null>(null);

    const period = periods.find(({ id }) => id === activePeriod);

    const [isOpen, setIsOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPeriodClosed, setRestrictToCollective] = useState(false);
    const [modalType, setModalType] = useState("");
    const [isClosingModalOpen, setIsClosingModalOpen] = useState(false);
    const [tabIdStatement, setTabIdStatement] = useState<number | null>(null);
    const [currentBeneficiaryId, setCurrentBeneficiaryId] = useState(0);

    const { getCompanySettings, companyInfos, currencySymbol: companyCurrencySymbol } = useCompanySettings();
    const usesAdvanceCompensation = getCompanySettings("uses_interterm_compensation");
    const usesMultipleCurrencies = getCompanySettings("uses_multiple_currencies");

    const { needRefresh } = useAppSelector((state) => state.management);
    const { termGroups } = useAppSelector((state) => state.beneficiary);

    const [balanceUpdateSuccessful, setBalanceUpdateSuccessful] = useState<{ termId: number | null; animate: boolean }>(
        { termId: null, animate: false },
    );

    const handleClose = (id: number) => {
        setAnchorEls((prev) => {
            const { [id]: toDelete, ...rest } = prev;
            return rest;
        });
    };

    const toggleDrawer: TToggleDrawer = (anchor, open) => (event) => {
        if (
            event &&
            event.type === "keydown" &&
            ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")
        ) {
            return;
        }
        setIsOpen(open);
        if (needRefresh) handleRequest();
        if (activePeriod) getStats(activePeriod);
        setTabIdStatement !== null && setTabIdStatement(null);
    };

    const handleCloseSingleTerm = useCallback(
        (termId: number) => {
            closeTerm([termId]);
            setIsClosingModalOpen(false);
            setCurrentTerm(null);
        },
        [closeTerm],
    );

    const handleSetAllSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedRows(generalTabData.map((item) => item.id));
            setAllSelected(true);
        } else {
            setSelectedRows([]);
            setAllSelected(false);
        }
    };

    const isTermDisabled = (term: GeneralTabTerm) => Boolean(term?.isClosed);

    const canAddSalesOnTerm = (term: GeneralTabTerm) => {
        if (periodClosed) return Boolean(term.hasCollectiveManagementChannel);
        return !isTermDisabled(term);
    };

    const checkAddingSales = useCallback(
        (term: GeneralTabTerm) => {
            addSale(term.titleId, term.id).then((res) => {
                const { payload } = res as { payload: { isPeriodClosed: boolean } | undefined };
                if (payload) {
                    setCurrentTerm(term);
                    setRestrictToCollective(payload.isPeriodClosed ?? false);
                    setIsModalOpen(true);
                }
            });
            handleClose(term.id);
        },
        [addSale],
    );

    const handleRecomputeTermRoyalties = useCallback(
        async (term: GeneralTabTerm) => {
            handleClose(term.id);
            if (!activePeriod) return;
            setRecomputingTermId(term.id);
            try {
                // The fulfilled reducer updates only this contract's row in generalTabData,
                // so we deliberately avoid a full table refresh (no flicker), like adding a sale.
                await dispatch(recomputeTermRoyalties({ periodId: activePeriod, termId: term.id }));
            } finally {
                setRecomputingTermId(null);
            }
        },
        [activePeriod, dispatch],
    );

    const handleShowStatement = useCallback((term: GeneralTabTerm, tabId: number) => {
        setTabIdStatement(tabId);
        setCurrentTerm(term);
        setIsOpen(true);
        handleClose(term.id);
    }, []);

    const handleGetCompensationGroupName = useCallback(
        (beneficiaryId: number) => {
            if (currentBeneficiaryId == beneficiaryId) return;
            dispatch(fetchTermGroups({ beneficiaryId: beneficiaryId })).then((resp: any) => {
                setCurrentBeneficiaryId(beneficiaryId);
            });
        },
        [currentBeneficiaryId],
    );

    useEffect(() => {
        if (closeTermsStatus === "pending") {
            setAllSelected(false);
            setSelectedRows([]);
        }
    }, [closeTermsStatus, setAllSelected, setSelectedRows]);

    useEffect(() => {
        setAllSelected(false);
    }, [activePeriod, setAllSelected]);

    useEffect(() => {
        setAllSelected(false);
    }, [page]);

    /** "Mettre à jour le solde d'ouverture" only — à-valoir uses the other menu item (setModalType "advance"). */
    const handleOpeningBalanceModal = (term: GeneralTabTerm) => {
        setBalanceUpdateSuccessful({ termId: null, animate: false });
        setModalType("override");
        setCurrentTerm(term);
        handleClose(term.id);
    };
    const visibleColumns = columns.filter((col) => showColumn(col.id));
    const skeletonColumnCount = visibleColumns.length + 2;
    const termGroupNameById = useMemo(() => new Map(termGroups.map((group) => [group.id, group.name])), [termGroups]);
    const selectedRowIds = useMemo(() => new Set(selectedRows), [selectedRows]);

    const isTableLoading =
        termsLoadingStatus === "pending" ||
        isRoyaltiesRecalculating ||
        (termsLoadingStatus === "idle" && generalTabData.length === 0 && activePeriod !== undefined);

    const showEmptyState =
        generalTabData.length === 0 && !isTableLoading && termsLoadingStatus !== "idle" && activePeriod !== undefined;

    const tableContent = useCallback(() => {
        if (!activePeriod) {
            return null;
        }

        return (
            <>
                {generalTabData.map((term, termIndex) => {
                    const compensationGroupName = term?.compensationGroupId
                        ? termGroupNameById.get(term.compensationGroupId) ?? ""
                        : "";

                    const isPreviousLineCompensationGroup =
                        term?.compensationGroupId &&
                        generalTabData[termIndex - 1]?.compensationGroupId === term?.compensationGroupId;
                    const isNextLineCompensationGroup =
                        term?.compensationGroupId &&
                        generalTabData[termIndex + 1]?.compensationGroupId === term?.compensationGroupId;

                    const balanceMenuDisabled = isTermDisabled(term) || periodFuture;
                    const balanceMenuTooltip = periodFuture ? t("Management.FuturePeriodDescription") : "";

                    const animateBalanceCell =
                        balanceUpdateSuccessful.termId === term.id && balanceUpdateSuccessful.animate;

                    const balancePrevAmountClassName = classNames({
                        "balance-prev-amount--modified": !!term.balanceModified,
                        "balance-update-animation": animateBalanceCell,
                    });

                    const isRecomputing = recomputingTermId === term.id;

                    return (
                        <TableRow
                            sx={{
                                backgroundColor: selectedRowIds.has(term.id) ? "#FAF9FF" : "white",
                            }}
                            tabIndex={-1}
                            key={`${term.id}${termIndex}`}
                            className={classNames("table-row", {
                                "table-row--future-period": periodFuture,
                            })}
                            onClick={() => {
                                if (isRecomputing) return;
                                handleShowStatement(term, 0);
                                handleClose(term.id);
                            }}
                        >
                            <TableCell
                                sx={{ padding: "10px" }}
                                className={`checkbox-row ${
                                    isPreviousLineCompensationGroup ? "compensation-group-link before" : ""
                                } ${isNextLineCompensationGroup ? "compensation-group-link after" : ""}`}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <StyledCheckbox
                                    disabled={period?.isActive === 2}
                                    onChange={(e) => handleSelectRow(e, term.id)}
                                    checked={selectedRowIds.has(term.id)}
                                    size="small"
                                />
                            </TableCell>
                            {visibleColumns.map((column) => {
                                const columnData = term[column.id]?.toString();
                                return (
                                    <TableCell
                                        key={column.id + activePeriod}
                                        align={column.align}
                                        className="table-row-center"
                                        sx={{
                                            padding: "8px 0",
                                            opacity: isTermDisabled(term) ? "0.6" : "1",
                                        }}
                                    >
                                        {column.id === "title" && (
                                            <ManagementTableTitleComponent
                                                nav={() =>
                                                    term.workId
                                                        ? nav(`/works/${term.workId}`)
                                                        : term.titleId
                                                          ? nav(`/titles/${term.titleId}`)
                                                          : undefined
                                                }
                                                title={term.title}
                                                isbn={term.legacyTermTitleEan13}
                                                titleCoverUrl={term.titleAvatarUrl}
                                                rowIndex={termIndex}
                                                handleQuery={handleQuery}
                                            />
                                        )}
                                        {column.id === "author" && (
                                            <ManagementTableAuthorComponent
                                                author={term.author}
                                                representative={term.representative}
                                                beneficiaryId={term.beneficiarId}
                                                avatarUrl={term.beneficiaryAvatarUrl}
                                                lastSentAt={term.lastSentAt}
                                                rowIndex={termIndex}
                                                page="management"
                                                handleQuery={handleQuery}
                                            />
                                        )}

                                        {column.id === "balancePrev" &&
                                            (usesAdvanceCompensation && term.compensationGroupId ? (
                                                <Tooltip
                                                    title={
                                                        <>
                                                            {t("NewChannel.CompensationGroup")} :{" "}
                                                            {compensationGroupName ?? ""}
                                                            <br />
                                                            {t("Management.GroupBalancePrev")} :{" "}
                                                            {formatNumber((term.groupBalance ?? 0) / 100, {
                                                                isCurrency: true,
                                                            })}
                                                            <br />
                                                            {t("Management.GroupGrossDuty")} :{" "}
                                                            {formatNumber((term.groupGrossDuty ?? 0) / 100, {
                                                                isCurrency: true,
                                                            })}
                                                            <br />
                                                            {t("Management.GroupNextBalance")} :{" "}
                                                            {formatNumber((term.groupNextBalance ?? 0) / 100, {
                                                                isCurrency: true,
                                                            })}
                                                        </>
                                                    }
                                                >
                                                    <div
                                                        className={classNames({
                                                            "balance-prev": true,
                                                            "balance-compensation": true,
                                                            before: isPreviousLineCompensationGroup,
                                                            after: isNextLineCompensationGroup,
                                                        })}
                                                        onMouseEnter={() =>
                                                            handleGetCompensationGroupName(term.beneficiarId)
                                                        }
                                                    >
                                                        {term.balanceModified ? (
                                                            <Tooltip title={t("BalanceUpdate.BalanceModified")}>
                                                                <span className={balancePrevAmountClassName}>
                                                                    {column.format && column?.format(columnData)}
                                                                </span>
                                                            </Tooltip>
                                                        ) : (
                                                            <span className={balancePrevAmountClassName}>
                                                                {column.format && column?.format(columnData)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </Tooltip>
                                            ) : (
                                                <div
                                                    ref={balanceCell}
                                                    id={`general-table-row-${termIndex}-balance-prev`}
                                                    className="balance-prev"
                                                >
                                                    {term.balanceModified ? (
                                                        <Tooltip title={t("BalanceUpdate.BalanceModified")}>
                                                            <span className={balancePrevAmountClassName}>
                                                                {column.format && column?.format(columnData)}
                                                            </span>
                                                        </Tooltip>
                                                    ) : (
                                                        <span className={balancePrevAmountClassName}>
                                                            {column.format && column?.format(columnData)}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        {column.id === "grossDuty" &&
                                            (usesAdvanceCompensation && term.compensationGroupId ? (
                                                <Tooltip
                                                    title={
                                                        <>
                                                            {t("NewChannel.CompensationGroup")} :{" "}
                                                            {compensationGroupName ?? ""}
                                                            <br />
                                                            {t("Management.GroupBalancePrev")} :{" "}
                                                            {formatNumber((term.groupBalance ?? 0) / 100, {
                                                                isCurrency: true,
                                                            })}
                                                            <br />
                                                            {t("Management.GroupGrossDuty")} :{" "}
                                                            {formatNumber((term.groupGrossDuty ?? 0) / 100, {
                                                                isCurrency: true,
                                                            })}
                                                            <br />
                                                            {t("Management.GroupNextBalance")} :{" "}
                                                            {formatNumber((term.groupNextBalance ?? 0) / 100, {
                                                                isCurrency: true,
                                                            })}
                                                        </>
                                                    }
                                                >
                                                    <div
                                                        className={`gross-duty-compensation ${
                                                            isPreviousLineCompensationGroup ? "before" : ""
                                                        } ${isNextLineCompensationGroup ? "after" : ""}`}
                                                        onMouseEnter={() =>
                                                            handleGetCompensationGroupName(term.beneficiarId)
                                                        }
                                                    >
                                                        {column.format && column?.format(columnData)}
                                                    </div>
                                                </Tooltip>
                                            ) : (
                                                <div
                                                    id={`general-table-row-${termIndex}-grossDuty`}
                                                    className={
                                                        term.underTheMinimum
                                                            ? "box red"
                                                            : term["compensate"] === 0
                                                              ? "box orange"
                                                              : "box yellow"
                                                    }
                                                >
                                                    {column.format && column?.format(columnData)}
                                                </div>
                                            ))}

                                        <div
                                            className={column.class}
                                            id={`general-table-row-${termIndex}-${column.id}`}
                                        >
                                            {!["title", "author", "balancePrev", "grossDuty"].includes(column.id)
                                                ? column.format && column.format(columnData)
                                                : ""}
                                        </div>
                                    </TableCell>
                                );
                            })}
                            <TableCell align="center" style={{ padding: 0 }} onClick={(e) => e.stopPropagation()}>
                                {isRecomputing ||
                                [...addSalesCheckingStatus, ...addSalesPendingStatus].includes(term.id) ? (
                                    <CircularProgress size="1.4rem" sx={{ color: variables["primary-purple"] }} />
                                ) : (
                                    <TableControl
                                        anchorEls={anchorEls}
                                        id={term.id}
                                        setAnchorEls={setAnchorEls}
                                        handleClose={handleClose}
                                    >
                                        <MenuItem
                                            disabled={!canAddSalesOnTerm(term) || isRoyaltiesRecalculating}
                                            id={`general-table-row-${term.id}-add-sales-btn`}
                                            onClick={() => {
                                                checkAddingSales(term);
                                            }}
                                        >
                                            <CurrencyIcon
                                                style={{ width: "0.87rem" }}
                                                currency={companyCurrencySymbol.length > 1 ? "USD" : undefined}
                                            />
                                            <span>{t("Management.AddCopyrights")}</span>
                                        </MenuItem>

                                        <MenuItem
                                            onClick={() => {
                                                handleShowStatement(term, 0);
                                                handleClose(term.id);
                                            }}
                                        >
                                            <Document />
                                            <span>{t("Management.ShowStatement")}</span>
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => {
                                                handleShowStatement(term, 1);
                                                handleClose(term.id);
                                            }}
                                        >
                                            <Visibility style={{ width: "1rem" }} />
                                            <span>{t("Management.SeeDetails")}</span>
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                term.workId
                                                    ? nav(`/works/${term.workId}`, {
                                                          state: { tab: "2", termId: term.id },
                                                      })
                                                    : term.titleId
                                                      ? nav(`/titles/${term.titleId}`, {
                                                            state: { tab: "2", termId: term.id },
                                                        })
                                                      : undefined
                                            }
                                        >
                                            <EastIcon style={{ width: "1rem" }} />
                                            <span>{t("Management.SeeContract")}</span>
                                        </MenuItem>
                                        <MenuItem
                                            disabled={isTermDisabled(term) || periodFuture}
                                            onClick={() => {
                                                openProvisionModal(term);
                                                handleClose(term.id);
                                            }}
                                        >
                                            <ProvisionMenuIcon />
                                            <span>{t("Management.ModifyProvision")}</span>
                                        </MenuItem>
                                        <MenuItem
                                            disabled={isTermDisabled(term) || periodFuture}
                                            onClick={() => {
                                                setModalType("advance");
                                                setCurrentTerm(term);
                                                handleClose(term.id);
                                            }}
                                        >
                                            <BankCard width={"16px"} />
                                            <span>
                                                {term.advancePaymentId || term.hasAdvances
                                                    ? t("BalanceUpdate.ManagementTable.UpdateAdvance")
                                                    : t("BalanceUpdate.ManagementTable.AddAdvance")}
                                            </span>
                                        </MenuItem>
                                        <ManagementModifyBalanceMenuItem
                                            disabled={balanceMenuDisabled}
                                            tooltipTitle={balanceMenuTooltip}
                                            onActivate={() => handleOpeningBalanceModal(term)}
                                        />
                                        <MenuItem
                                            disabled={
                                                isTermDisabled(term) ||
                                                periodClosed ||
                                                isRoyaltiesRecalculating ||
                                                recomputingTermId === term.id
                                            }
                                            onClick={() => handleRecomputeTermRoyalties(term)}
                                        >
                                            <CurrencyExchangeIcon style={{ width: "1rem" }} />
                                            <span>{t("Management.RecomputeRoyalties")}</span>
                                        </MenuItem>
                                        <MenuItem
                                            sx={{ color: "#B55D5D" }}
                                            disabled={isTermDisabled(term) || periodFuture}
                                            onClick={() => {
                                                handleClose(term.id);
                                                setCurrentTerm(term);
                                                setIsClosingModalOpen(true);
                                            }}
                                        >
                                            <NotAllow />
                                            <span>{t("Management.CloseTerm")}</span>
                                        </MenuItem>
                                    </TableControl>
                                )}
                                {!!term.isClosed && <div className="table-row-closed" />}
                                {periodFuture && !term.isClosed && <div className="table-row-future" />}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </>
        );
    }, [
        activePeriod,
        generalTabData,
        selectedRowIds,
        termGroupNameById,
        periodFuture,
        period?.isActive,
        addSalesCheckingStatus,
        addSalesPendingStatus,
        anchorEls,
        t,
        handleSelectRow,
        checkAddingSales,
        handleShowStatement,
        handleRecomputeTermRoyalties,
        recomputingTermId,
        periodClosed,
        isRoyaltiesRecalculating,
        nav,
        setSelectedRows,
        openProvisionModal,
    ]);

    return (
        <div className="management-table-container">
            <ModalBase
                onClose={() => setIsClosingModalOpen(false)}
                open={isClosingModalOpen}
                modalTitle={t("Management.EndContract")}
            >
                <div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                        <SmallBody>
                            {t("Management.ConfirmEndContract", {
                                title: currentTerm?.title,
                                author: currentTerm?.author,
                            })}
                        </SmallBody>
                        <SmallBody color="danger">{t("Management.ConfirmEndContract1")}</SmallBody>
                    </div>
                    <MessageBlock
                        type="warning"
                        text={t("Management.IrreversibleDecision")}
                        small
                        style={{ marginBlock: "1rem" }}
                    />
                    <StyledButton
                        buttonType="primary"
                        fullWidth
                        onClick={() => {
                            if (currentTerm) {
                                handleCloseSingleTerm(currentTerm.id);
                            }
                        }}
                    >
                        {t("ImportData.Confirm")}
                    </StyledButton>
                </div>
            </ModalBase>
            {currentTerm && (
                <ModalBox
                    onClose={() => setIsModalOpen(false)}
                    open={isModalOpen}
                    mainTitle={t("AddSales.Title")}
                    helpdeskInfo={{
                        text: t("AddSales.SalesInfo"),
                        link: "https://helpdesk.crealo.app/fr/article/ajouter-des-ventes-10nni0m/",
                    }}
                >
                    <AddSalesModal
                        termId={currentTerm.id}
                        onClose={() => setIsModalOpen(false)}
                        from="table"
                        isPeriodClosed={isPeriodClosed}
                        currentPeriodId={activePeriod}
                    />
                </ModalBox>
            )}
            <BalanceUpdateModal
                term={currentTerm}
                option={modalType as "override" | "advance"}
                setModalType={setModalType}
                currentPeriod={period}
                setBalanceUpdateSuccessful={setBalanceUpdateSuccessful}
                refetchGeneralTab={handleRequest}
            />
            {currentTerm && (
                <ManagementTitleDetails
                    currentTerm={currentTerm}
                    isOpen={isOpen}
                    setOpen={setIsOpen}
                    toggleDrawer={toggleDrawer}
                    tabId={tabIdStatement}
                    activePeriod={activePeriod}
                    usesMultipleCurrencies={usesMultipleCurrencies}
                    companyInfos={companyInfos}
                />
            )}
            {periodClosed && (
                <MessageBlock
                    type="info"
                    text={t("Management.SalesClosedInfo")}
                    small
                    style={{ marginBottom: "10px" }}
                    link="https://helpdesk.crealo.app/fr/article/cloturer-une-periode-xhf5s5/"
                />
            )}
            {periodFuture && (
                <MessageBlock
                    type="info"
                    text={t("Management.FuturePeriodDescription")}
                    small
                    style={{ marginBottom: "10px" }}
                />
            )}
            <Paper
                sx={{
                    width: "100%",
                    // overflow: "hidden",
                    marginBottom: "20px",
                    border: `1px solid ${variables["border-grey"]}`,
                    borderRadius: variables["border-raduis"],
                    boxShadow: "none",
                }}
            >
                <TableContainer className="management-table">
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        paddingInline: "10px",
                                        paddingBlock: "8px",
                                        backgroundColor: "#F6F6F6",
                                        textAlign: "center",
                                    }}
                                >
                                    <StyledCheckbox
                                        disabled={period?.isActive === 2}
                                        checked={allSelected}
                                        value={allSelected}
                                        onChange={handleSetAllSelected}
                                        size="small"
                                    />
                                </TableCell>
                                {activePeriod &&
                                    visibleColumns.map((column) => (
                                        <TableCell
                                            id={`table-head-cell-sortBy-${column.id}`}
                                            key={column.id + activePeriod}
                                            align={column.align}
                                            sx={{
                                                minWidth: column.minWidth,
                                                ...tHeadStyles,
                                            }}
                                        >
                                            <TableSortLabel
                                                active={sortBy === column.id}
                                                direction={sortOrder.toLowerCase() as "asc" | "desc"}
                                                onClick={() => handleSort(column.id)}
                                                sx={{ lineHeight: "12px" }}
                                            >
                                                <div
                                                    style={
                                                        column.id === "author"
                                                            ? {
                                                                  display: "flex",
                                                                  flexDirection: "column",
                                                                  alignItems: "flex-start",
                                                              }
                                                            : {}
                                                    }
                                                >
                                                    {column.id === "title"
                                                        ? t("Works.WorkLabel")
                                                        : t(
                                                              `Management.${
                                                                  column.id[0].toUpperCase() + column.id.slice(1)
                                                              }`,
                                                          )}
                                                    {column.id === "author" && (
                                                        <span style={{ fontSize: "10px" }}>
                                                            {t(`Management.Representative`)}
                                                        </span>
                                                    )}
                                                </div>
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                <TableCell sx={{ ...tHeadStyles, padding: "0 12px" }} align="center">
                                    <button
                                        onClick={() =>
                                            handleRequest({
                                                isInitialLoad: false,
                                                behavior: "refresh",
                                                extraOptions: { periodId: activePeriod },
                                            })
                                        }
                                        title={`${t("AdvancePayment.Refresh")}`}
                                        style={{
                                            display: "flex",
                                            border: "none",
                                            backgroundColor: "transparent",
                                            cursor: "pointer",
                                            margin: "auto",
                                        }}
                                    >
                                        <ReplayOutlined sx={{ color: "#676767", fontSize: "1.2rem" }} />
                                    </button>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isTableLoading ? (
                                <TableSkeletonRows
                                    rowCount={Math.min(limitPagination, TABLE_SKELETON_MAX_ROWS)}
                                    columnCount={skeletonColumnCount}
                                    withCheckboxColumn
                                />
                            ) : (
                                tableContent()
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {showEmptyState && (
                    <div className="management-table-empty">
                        <EmptyState type="titleBoard" sx={{ margin: "auto" }} />
                    </div>
                )}

                <TablePagination
                    component={() => (
                        <Pagination
                            rowsPerPageOptions={[10, 25, 100]}
                            rowsPerPage={limitPagination}
                            currentPageSize={generalTabData.length}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeLimit}
                            type="terms"
                            count={count}
                            sticky={true}
                        />
                    )}
                    count={-1}
                    rowsPerPage={limitPagination}
                    page={page}
                    onPageChange={handleChangePage}
                />
            </Paper>
        </div>
    );
}

export default ManagementGeneralTable;
